<script lang="ts" context="module">
	import { fetchUtils } from '../utils/fetchUtils'
	import { t } from '../../i18n/i18n'
	import { type Notification, NotificationType } from '../types/notification'
	import { addNotification } from '../stores'
	import { get } from 'svelte/store'
	import { LotState } from '../types/enums'

	export function savePublicComment(remarks: string, lotPublicId: string): void {
		let request = { publicComment: remarks }
		updateLotComments(request, 'PublicComment', lotPublicId)
	}

	function updateLotComments(
		request: { publicComment: string },
		urlSuffix: string,
		lotPublicId: string
	) {
		fetchUtils
			.post('/api/offerer/lot/' + lotPublicId + '/update' + urlSuffix, request)
			.then((_) => {
				let notification: Notification = {
					message: get(t)('UI.notification.saved'),
					type: NotificationType.SUCCESS,
					timeout: 4000,
					dismissible: false
				}
				addNotification(notification)
			})
			.catch((error) => {
				let notification: Notification = {
					message: get(t)('UI.error.save') + ': ' + error.statusText,
					type: NotificationType.ERROR,
					timeout: 5000,
					dismissible: true
				}
				addNotification(notification)
			})
	}

	export async function updateAttachmentsLot(id: string | undefined) {
		let res
		await fetchUtils
			.get('/api/lot/pub/' + id + '/attachments')
			.then((data) => {
				res = data
			})
			.catch((error) => console.log(error))
		return res
	}
</script>

<script lang="ts">
	import { onMount, setContext } from 'svelte'
	import type { Lot } from '../types/lot'
	import OffererPubPageApproval from '../offerer/OffererPubPageApproval.svelte'
	import OffererPubPageBidding from '../offerer/OffererPubPageBidding.svelte'
	import OffererPubPageOptimized from '../offerer/OffererPubPageOptimized.svelte'
	import OffererPubPageHandling from '../offerer/OffererPubPageHandling.svelte'
	import OffererPubPageAccounting from '../offerer/OffererPubPageAccounting.svelte'
	import PubPageFailed from './PubPageFailed.svelte'
	import PubPageCompleted from './PubPageCompleted.svelte'
	import {
		redirectToLoginOnLoggedOutHandled404,
		redirectToNotFoundRoute
	} from '../utils/errorRedirect'
	import { contactData } from '../stores'
	import { DefaultNotification } from '../types/notification'
	import { type Writable, writable } from 'svelte/store'
	import { OFFERER_PAYS_KICKBACK } from '../types/enums'
	import { Facts } from '../bid/types/facts'
	import { type RequestDto } from '../bid/types/requestDto'

	export let requestPublicId: string | undefined = undefined
	export let offerUserPublicId: string | undefined = undefined
	export let lotPublicId: string | undefined = undefined

	setContext('urlParams', {
		get lotPublicId() {
			return lotPublicId
		},
		get offerUserPublicId() {
			return offerUserPublicId
		},
		get requestPublicId() {
			return requestPublicId
		}
	}) // getters ensure current prop values are returned even in Svelte 5 mixed-mode interop
	setContext('isSaasInternalLot', writable(false))
	let showKickback: Writable<boolean> = setContext('showKickback', writable(false))
	const request: Writable<RequestDto | null> = setContext('request', writable(null))
	const lot: Writable<Lot | null> = setContext('lot', writable(null))

	const components = {
		[LotState.APPROVAL]: OffererPubPageApproval,
		[LotState.BIDDING]: OffererPubPageBidding,
		[LotState.OPTIMIZED]: OffererPubPageOptimized,
		[LotState.HANDLING]: OffererPubPageHandling,
		[LotState.ACCOUNTING]: OffererPubPageAccounting,
		[LotState.TARGET_ACTUAL_VERIFICATION]: OffererPubPageAccounting,
		[LotState.DISCARDED]: PubPageFailed,
		[LotState.COMPLETED]: PubPageCompleted
	}
	let component: (typeof components)[keyof typeof components] | undefined = OffererPubPageApproval

	onMount(async () => {
		await fetchLotData(lotPublicId)
	})

	async function fetchLotData(id: string | undefined) {
		// FIXME: id per definition is never falsy, hence the linter argues that
		// the nullish operator never reaches the right side
		// we should be defensive here and check if id exists
		// if yes, the construct the url before we pass it to fetchUtils
		await fetchUtils
			.get('/api/offerer/lot/' + id ?? lotPublicId)
			.then((data) => {
				$lot = data ? { ...data } : null
				//after accepting an offer or counter offer we get the lotPublicId of the resulting lot (can differ due to shortage)
				// but as we have the id of the matching quote, we can still show the data
				Object.assign($contactData, data.contactData)
				$contactData = $contactData
				const mapped = components[$lot!.state as keyof typeof components]
				// the map covers 8 of the 14 lot states, and state may be undefined — without
				// this the page keeps a lot it cannot render and shows an empty shell
				if (!mapped) {
					// drop the initial component too, or the gate below mounts the approval page
					component = undefined
					redirectToNotFoundRoute()
					return
				}
				component = mapped
			})
			.catch(async (error) => {
				console.log(error)
				const message = typeof error === 'string' ? error : (error?.message ?? get(t)('Error'))
				let notification: Notification = DefaultNotification(
					message,
					NotificationType.ERROR,
					true,
					0
				)
				addNotification(notification)
				if (await redirectToLoginOnLoggedOutHandled404(error)) return
				// single-owner rule: doHandle already handled 401/403/410 terminally;
				// only unhandled codes fall back to the designed /404 route — the
				// {#if $lot} gate below never opens on failure (see REPORT.md)
				if (!error?.handled) {
					redirectToNotFoundRoute()
				}
			})
	}

	$: ($lot,
		$lot?.type,
		($showKickback = $lot?.type ? OFFERER_PAYS_KICKBACK.includes($lot.type) : false))
</script>

{#if $lot}
	<svelte:component
		this={component}
		on:updateLot={(e: CustomEvent<{ lotPublicId: string }>) => fetchLotData(e.detail.lotPublicId)}
	/>
{/if}
